import React from "react";

import Grid from "@material-ui/core/Grid";

import { SubtleBadge } from "components/Badge";
import { FormCollapse } from "components/Form";
import { Row, SpaceBetweenRow } from "components/Layout";

import { FinanceSubModalSection } from "constants/settings";

import {
  SectionToggleState,
  useBoolean,
  useFieldValue,
  useSectionHelper,
} from "hooks";
import { Label, withNamespace } from "components/Form/FormikControls";
import { FieldArray } from "formik";
import { useSelector } from "react-redux";
import { getDefaultBillingSettings } from "selectors";
import { SlimButton } from "components/Button";
import { ButtonIcon } from "components/Button/FontAwesomeButton";
import { faPlus, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { Box, Divider } from "@material-ui/core";
import {
  SaleSubTypeField,
  SaleTypeField,
  SpeciesField,
} from "components/Form/Fields/SaleFilter";
import { v4 as uuidv4 } from "uuid";
import { BankingDetailField } from "components/Form/Fields/BankingDetailField";
import { ConfirmDialog } from "components/ConfirmDialog";
import { Tooltip } from "components/Form/FormikControls/Tooltip";

interface SectionProps {
  sectionHelper: SectionToggleState;
  namespace: string;
}

interface PaymentPreferenceProps {
  index: number;
  name: string;
  namespace: string;
  removeRow: (index: number) => null;
}

function PaymentPreference(props: PaymentPreferenceProps) {
  const { index, name, namespace, removeRow } = props;
  const ns = withNamespace(withNamespace(namespace, name), index.toString());

  const [isConfirmDialogOpen, openConfirmDialog, closeConfirmDialog] =
    useBoolean();
  return (
    <>
      <Grid item xs={6} md={3}>
        <SaleTypeField namespace={ns} required />
      </Grid>
      <Grid item xs={6} md={2}>
        <SpeciesField namespace={ns} required />
      </Grid>
      <Grid item xs={6} md={3}>
        <SaleSubTypeField namespace={ns} />
      </Grid>
      <Grid item xs={5} md={3}>
        <BankingDetailField namespace={ns} required />
      </Grid>

      <Grid item xs={1} md={1} container justifyContent="flex-end">
        <Box>
          <SlimButton
            onClick={openConfirmDialog}
            backgroundColor="deleteRed"
            type="button"
          >
            <ButtonIcon icon={faTrash} />
          </SlimButton>
        </Box>
        <ConfirmDialog
          title="Are you sure?"
          isOpen={isConfirmDialogOpen}
          onCancel={closeConfirmDialog}
          onDelete={() => removeRow(index)}
        />
      </Grid>

      <Grid xs={12} item>
        <Divider />
        <SpaceBetweenRow />
      </Grid>
    </>
  );
}

const generateDefaultPaymentPreference = () => ({
  id: uuidv4(),
  saleType: null,
  speciesId: null,
  saleSubTypeId: null,
  bankingDetailId: null,
});

export const PaymentPreferencesSection = ({
  sectionHelper,
  namespace: ns,
}: SectionProps) => {
  const { isOpen, onToggle } = useSectionHelper(
    sectionHelper,
    FinanceSubModalSection.PAYMENT_PREFERENCES,
  );

  const name = "paymentPreferences";

  const header = (
    <Row>
      <SubtleBadge>Payment Preferences </SubtleBadge>
    </Row>
  );

  const paymentPreferences = useFieldValue(withNamespace(ns, name)) || [];
  const billingSettingsId = useSelector(getDefaultBillingSettings)?.id;

  if (!billingSettingsId) {
    return null;
  }

  return (
    <FormCollapse isOpen={isOpen} onToggle={onToggle} header={header}>
      <Grid item xs={12}>
        <FieldArray name={withNamespace(ns, "paymentPreferences")}>
          {arrayHelpers => {
            const addNew = () => {
              arrayHelpers.push(generateDefaultPaymentPreference());
            };

            return (
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="space-between">
                    <SlimButton onClick={addNew} type="button">
                      <ButtonIcon icon={faPlus} />
                      &nbsp;New Payment Preference
                    </SlimButton>
                    <Tooltip title="Add preferences for when a Bank Account will be displayed on an Invoice/used for receiving payments." />
                  </Box>
                </Grid>

                {paymentPreferences.length > 0 && (
                  <>
                    <Grid item xs={6} md={3}>
                      <Label>Sale Type</Label>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <Label>Species</Label>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Label>Sale Sub Type</Label>
                    </Grid>
                    <Grid item xs={5} md={3}>
                      <Label>Bank Account</Label>
                    </Grid>

                    <Grid item xs={1} md={1}>
                      &nbsp;
                    </Grid>

                    {paymentPreferences.map(
                      (paymentPreference: PaymentPreference, idx: number) => (
                        <PaymentPreference
                          key={paymentPreference.id || idx}
                          index={idx}
                          removeRow={arrayHelpers.remove}
                          name={name}
                          namespace={ns}
                        />
                      ),
                    )}
                  </>
                )}
              </Grid>
            );
          }}
        </FieldArray>
      </Grid>
    </FormCollapse>
  );
};
